import { useState } from 'react';
import type { SaRole, PermissionEngine, PermissionAction } from '@/mocks/saRolesPermissions';
import { ENGINE_LABELS, ALL_ENGINES, ALL_ACTIONS, CRITICAL_ACTIONS } from '@/mocks/saRolesPermissions';
import { ENGINE_ICONS, ACTION_ICONS, ACTION_COLORS } from './roleLabels';

interface Props {
  role: SaRole;
  readOnly?: boolean;
  onPermissionChange?: (engine: PermissionEngine, action: PermissionAction, granted: boolean) => void;
}

export default function PermissionMatrix({ role, readOnly = false, onPermissionChange }: Props) {
  const [expandedEngines, setExpandedEngines] = useState<Set<PermissionEngine>>(new Set(ALL_ENGINES));

  const toggleEngine = (engine: PermissionEngine) => {
    setExpandedEngines((prev) => {
      const next = new Set(prev);
      if (next.has(engine)) next.delete(engine); else next.add(engine);
      return next;
    });
  };

  const expandAll = () => setExpandedEngines(new Set(ALL_ENGINES));
  const collapseAll = () => setExpandedEngines(new Set());

  const hasAction = (engine: PermissionEngine, action: PermissionAction): boolean => {
    const entry = role.permissions.find((p) => p.engine === engine);
    if (!entry) return false;
    const override = role.overrides.find((o) => o.engine === engine && o.action === action);
    if (override) return override.granted;
    return entry.actions.includes(action);
  };

  const engineActionCount = (engine: PermissionEngine): number => {
    return ALL_ACTIONS.filter((a) => hasAction(engine, a)).length;
  };

  const engineCriticalCount = (engine: PermissionEngine): number => {
    return CRITICAL_ACTIONS.filter((a) => hasAction(engine, a)).length;
  };

  return (
    <div className="space-y-2">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Matriz de Permisos</span>
          <span className="px-1.5 py-0.5 rounded-full bg-background-200 text-foreground-500 text-[9px] font-bold">
            {ALL_ENGINES.length} engines
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={expandAll}
            className="px-2 py-1 rounded-lg text-[10px] font-medium text-foreground-500 bg-background-100 cursor-pointer hover:bg-background-200 hover:text-foreground-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-expand-vertical-line mr-1"></i>
            Expandir todo
          </button>
          <button
            onClick={collapseAll}
            className="px-2 py-1 rounded-lg text-[10px] font-medium text-foreground-500 bg-background-100 cursor-pointer hover:bg-background-200 hover:text-foreground-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-collapse-vertical-line mr-1"></i>
            Colapsar todo
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-background-100/70 flex-wrap">
        <span className="text-foreground-400 text-[10px] font-medium">Leyenda:</span>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500"></div>
          <span className="text-foreground-500 text-[10px]">Permitido</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-background-300 border border-background-300"></div>
          <span className="text-foreground-500 text-[10px]">Denegado</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-rose-500/30 border border-rose-300"></div>
          <span className="text-foreground-500 text-[10px]">Acceso crítico</span>
        </div>
      </div>

      {/* Engine rows */}
      <div className="space-y-1.5">
        {ALL_ENGINES.map((engine) => {
          const isExpanded = expandedEngines.has(engine);
          const count = engineActionCount(engine);
          const critical = engineCriticalCount(engine);

          return (
            <div key={engine} className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
              {/* Engine header */}
              <button
                onClick={() => toggleEngine(engine)}
                className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-background-100/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center shrink-0">
                  <i className={`${ENGINE_ICONS[engine]} text-foreground-600 text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <span className="text-foreground-800 text-sm font-semibold block">{ENGINE_LABELS[engine]}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-foreground-400 text-[10px]">{count}/{ALL_ACTIONS.length}</span>
                  {critical > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 text-[9px] font-bold">
                      {critical} crítico
                    </span>
                  )}
                  <div className="w-5 h-5 rounded flex items-center justify-center">
                    <i className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400 text-sm`}></i>
                  </div>
                </div>
              </button>

              {/* Actions grid */}
              {isExpanded && (
                <div className="px-4 pb-3 pt-0">
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-12 gap-1.5">
                    {ALL_ACTIONS.map((action) => {
                      const granted = hasAction(engine, action);
                      const isCritical = CRITICAL_ACTIONS.includes(action);

                      return (
                        <button
                          key={action}
                          onClick={() => {
                            if (!readOnly && onPermissionChange) {
                              onPermissionChange(engine, action, !granted);
                            }
                          }}
                          disabled={readOnly}
                          className={`flex flex-col items-center gap-1 px-2 py-2 rounded-xl text-center transition-all ${
                            readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-105'
                          } ${
                            granted
                              ? isCritical
                                ? 'bg-rose-50 border border-rose-200'
                                : 'bg-emerald-50 border border-emerald-200'
                              : 'bg-background-100 border border-background-300'
                          }`}
                          title={`${action}${isCritical ? ' (Crítico)' : ''}: ${granted ? 'Permitido' : 'Denegado'}${readOnly ? '' : ' — Click para toggle'}`}
                        >
                          <i
                            className={`${ACTION_ICONS[action]} text-sm`}
                            style={{ color: granted ? ACTION_COLORS[action] : '#9ca3af' }}
                          ></i>
                          <span
                            className={`text-[9px] font-semibold leading-tight whitespace-nowrap`}
                            style={{ color: granted ? ACTION_COLORS[action] : '#9ca3af' }}
                          >
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                          </span>
                          {isCritical && granted && (
                            <span className="w-1 h-1 rounded-full bg-rose-500"></span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}